import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toastr } from 'react-redux-toastr';

import { useAdmin } from './AdminContext';
import { adminApi, can, errorMessage } from './adminApi';
import { Dialog, Panel } from './kit';
import { copyText, formatDateTime, formatRelative } from './ui';
import { actionLabel } from './auditLabels';

const MIN_REASON = 10;

/** Pede o motivo (vai para a auditoria) e roda a acao. */
export function ReasonDialog({ title, text, confirmLabel, placeholder, onConfirm, onClose }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      toastr.error('Não foi possível concluir.', errorMessage(err, 'Tente de novo em instantes.'));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button type='button' className='ax-btn' onClick={onClose} disabled={busy}>Cancelar</button>
          <button type='submit' form='ax-reason-form' className='ax-btn ax-btn-primary' disabled={busy || reason.trim().length < MIN_REASON}>
            {busy ? 'Aguarde…' : confirmLabel}
          </button>
        </>
      )}
    >
      {text && <p>{text}</p>}
      <form id='ax-reason-form' onSubmit={submit}>
        <label className='ax-field'>
          <span>Motivo</span>
          <input value={reason} maxLength={300} placeholder={placeholder || 'Ex.: ticket 1234'} onChange={(e) => setReason(e.target.value)} />
          <small>Fica registrado na auditoria. Mínimo de {MIN_REASON} caracteres.</small>
        </label>
      </form>
    </Dialog>
  );
}

/** Confirmacao simples nomeando o alvo e a consequencia. */
export function ConfirmDialog({ title, text, confirmLabel, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      toastr.error('Não foi possível concluir.', errorMessage(err, 'Tente de novo em instantes.'));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button type='button' className='ax-btn' onClick={onClose} disabled={busy}>Cancelar</button>
          <button type='button' className='ax-btn ax-btn-primary' onClick={run} disabled={busy}>{busy ? 'Aguarde…' : confirmLabel}</button>
        </>
      )}
    >
      <p>{text}</p>
    </Dialog>
  );
}

/** Senha provisoria: motivo + senha do admin; mostra a senha uma vez so. */
export function TemporaryPasswordDialog({ targetName, onGenerate, onClose }) {
  const [reason, setReason] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { temporaryPassword } = await onGenerate(reason.trim(), adminPassword);
      setPassword(temporaryPassword);
    } catch (err) {
      toastr.error('Não foi possível gerar a senha.', errorMessage(err, 'Tente de novo em instantes.'));
    } finally {
      setBusy(false);
    }
  };

  if (password) {
    return (
      <Dialog title='Senha provisória gerada' onClose={onClose} footer={<button type='button' className='ax-btn ax-btn-primary' onClick={onClose}>Concluir</button>}>
        <p>Passe esta senha para {targetName} por um canal seguro. Ela aparece só agora, vale por 72 horas e, no primeiro acesso, a conta terá de criar uma senha nova.</p>
        <div className='ax-secret'>
          <code>{password}</code>
          <button type='button' className='ax-btn' onClick={() => copyText(password, 'Senha')}>Copiar</button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={`Gerar senha provisória para ${targetName}?`}
      onClose={onClose}
      footer={(
        <>
          <button type='button' className='ax-btn' onClick={onClose} disabled={busy}>Cancelar</button>
          <button type='submit' form='ax-temp-form' className='ax-btn ax-btn-danger' disabled={busy || reason.trim().length < MIN_REASON || !adminPassword}>
            {busy ? 'Gerando…' : 'Gerar senha'}
          </button>
        </>
      )}
    >
      <p>A senha atual deixa de valer, as sessões abertas caem e o titular recebe um e-mail avisando.</p>
      <form id='ax-temp-form' onSubmit={submit}>
        <label className='ax-field'>
          <span>Motivo</span>
          <input value={reason} maxLength={300} placeholder='Ex.: ticket 1234, cliente sem acesso ao e-mail' onChange={(e) => setReason(e.target.value)} />
        </label>
        <label className='ax-field'>
          <span>Sua senha do painel</span>
          <input type='password' autoComplete='current-password' value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} />
        </label>
      </form>
    </Dialog>
  );
}



/** Atividade recente de uma conta (auditoria filtrada). So o administrador ve a auditoria. */
export function ActivityPanel({ targetType, targetId, refreshKey }) {
  const { admin } = useAdmin();
  const [rows, setRows] = useState(null);
  const allowed = can(admin, 'super_admin');

  useEffect(() => {
    if (!allowed) return undefined;
    let active = true;
    adminApi.audit({ targetType, targetId, limit: 10 })
      .then((data) => { if (active) setRows(data); })
      .catch(() => { if (active) setRows([]); });
    return () => { active = false; };
  }, [allowed, targetType, targetId, refreshKey]);

  if (!allowed) return null;

  return (
    <Panel title='Atividade recente' action={<Link className='ax-link' to='/admin/audit'>Ver auditoria</Link>}>
      {!rows && <span className='ax-skeleton-line' aria-busy='true' />}
      {rows && rows.length === 0 && <p className='ax-muted'>Nenhuma ação registrada nesta conta.</p>}
      {rows && rows.length > 0 && (
        <ul className='ax-activity'>
          {rows.map((row) => (
            <li key={row.id}>
              <time dateTime={row.createdAt} title={formatDateTime(row.createdAt)}>{formatRelative(row.createdAt)}</time>
              <span>
                <strong className='ax-strong'>{admin && row.adminId === admin.id ? 'Você' : `Admin #${row.adminId}`}</strong> · {actionLabel(row.action)}
                {row.result === 'fail' && <span className='ax-badge is-danger'>falhou</span>}
                {row.details && row.details.reason && <q>{row.details.reason}</q>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
